import {Component, inject, signal} from '@angular/core';
import {confirm} from 'devextreme/ui/dialog';

import {FraisFixeDTO} from '../../../core/dtos/frais-fixe-dto';
import {FraisFixeService} from '../../../core/services/entities/frais-fixe.service';
import {ToastService} from '../../../core/services/technique/toast.service';
import {InternationalizationService} from '../../../core/services/i8n/i8n.service';
import {UtilsService} from '../../../core/utils/utils.service';
import {MSG_KEY, MSG_SEVERITY, TYPE_FRAIS_FIXE_LABELS} from '../../../core/constants';
import {ResponseWrapper} from '../../../core/suppliers/wrappers/response-wrapper';
import {IsDeletableObject} from '../../../core/models/is-deletable-object';
import {ButtonThemeEnum} from '../../../shared/ui/button/button-theme-enum';

@Component({
  selector: 'yo-fraisFixe',
  templateUrl: './frais-fixe.component.html',
  styleUrls: ['./frais-fixe.component.scss'],
  standalone: false
})
export class FraisFixeComponent {

  protected readonly ButtonThemeEnum = ButtonThemeEnum;

  readonly fraisFixeSvc = inject(FraisFixeService);
  readonly fraisFixeResource = this.fraisFixeSvc.fraisFixeResource();
  private readonly toastSvc = inject(ToastService);
  private readonly i8nSvc = inject(InternationalizationService);
  readonly utilsSvc = inject(UtilsService);

  selectedRows: number[] = [];
  readonly errors = signal<{ infosLine: string }[]>([]);
  readonly displayDeletePopupErrors = signal(false);
  readonly showDialog = signal(false);
  readonly editingDto = signal<FraisFixeDTO | null>(null);

  pathHelpFile: string = null;

  openDialogEdition = (dto: FraisFixeDTO | null): void => {
    this.editingDto.set(dto);
    this.showDialog.set(true);
  };

  onSaved = ({dto, isUpdate}: { dto: FraisFixeDTO; isUpdate: boolean }): void => {
    this.fraisFixeResource.update(current => {
      const list = current ?? [];
      return isUpdate
        ? list.map(f => f.id === dto.id ? dto : f)
        : [...list, dto];
    });
  };

  calculateSiteValue = (dto: FraisFixeDTO): string => dto?.site?.libelle ?? '';

  calculateTaxeValue = (element: FraisFixeDTO): String =>  {
    return element.taxe.libelle + ' - ' + element?.taxe?.valeur + '%'
  }

  calculateTypeValue = (dto: FraisFixeDTO): string =>
    TYPE_FRAIS_FIXE_LABELS[dto?.typeFraisFixe] ?? dto?.typeFraisFixe ?? '';

  canDelete = (): boolean => this.selectedRows.length > 0;

  deleteValues = async (): Promise<void> => {
    this.errors.set([]);
    let idsToDeleteInTable: number[] = [...this.selectedRows];

    const confirmed: boolean = await confirm(
      this.i8nSvc.getLabelFromCode(idsToDeleteInTable.length > 1 ? 'CONFIRM_DELETION_PLURAL' : 'CONFIRM_DELETION_SINGULAR', null),
      this.i8nSvc.getLabelFromCode('Suppression', null)
    );
    if (!confirmed) {
      return;
    }

    this.fraisFixeSvc.delete(idsToDeleteInTable).subscribe(response => {
      const res: any = (response as ResponseWrapper<IsDeletableObject>).one;

      if (res) {
        if (res.messagesErrorList && res.messagesErrorList.length > 0) {
          const current = this.fraisFixeResource.value() ?? [];
          for (const error of res.messagesErrorList) {
            const infosLine: string = this.i8nSvc.getLabelFromCode(error.code, error.args ? error.args.split(',') : null);
            this.errors.update(list => [...list, {infosLine}]);
            const elementToKeep = current.find(ff => ff.code === error.args);
            if (elementToKeep) {
              idsToDeleteInTable = idsToDeleteInTable.filter(id => elementToKeep.id !== id);
            }
          }
          this.displayDeletePopupErrors.set(true);
        } else {
          this.toastSvc.displayToast(
            MSG_KEY.ROOT,
            MSG_SEVERITY.SUCCESS,
            this.i8nSvc.getLabelFromCode(idsToDeleteInTable.length > 1 ? 'DELETION_COMPLETED_PLURAL' : 'DELETION_COMPLETED_SINGULAR', null)
          );
        }
        this.fraisFixeResource.update(
          current => (current ?? []).filter(ff => !idsToDeleteInTable.includes(ff.id))
        );
      }
      this.selectedRows = [];
    });
  };

  closeErrors = (): void => {
    this.errors.set([]);
    this.displayDeletePopupErrors.set(false);
  };
}
